import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
  Brain, Zap, MessageSquare, Eye, GitBranch, AlertTriangle,
  Shield, Users, ChevronDown, ChevronUp, ArrowRight, Check, X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const FAQS = [
  {
    q: 'Does Session Intelligence™ replace the coach?',
    a: 'No. Session Intelligence™ is an analytical support tool. It helps you organise what was said during a session and surfaces possible patterns worth exploring. Every insight requires your professional judgement and validation with the client. The relationship, presence, and skill remain entirely yours.',
  },
  {
    q: 'Is Session Intelligence™ a diagnostic or therapy tool?',
    a: 'No. Session Intelligence™ is a coaching analysis tool designed for professional coaches. It does not diagnose, assess, or treat mental health conditions. All output is framed as coaching hypotheses — possible patterns for you to explore, not conclusions.',
  },
  {
    q: 'What formats can I upload?',
    a: 'You can upload audio files (MP3, M4A, WAV, OGG, WebM — up to 200MB), documents (PDF, Word .docx, plain text), or enter session notes directly. Audio is automatically transcribed and then deleted from our servers after processing.',
  },
  {
    q: 'Do I need client consent before uploading?',
    a: 'Yes. Before uploading any recording, transcript, or notes, you must have explicit permission from your client for AI-assisted analysis. Session Intelligence™ includes a consent confirmation step in the upload flow as a reminder of this responsibility.',
  },
  {
    q: 'Can I discuss the report with an AI thinking partner?',
    a: 'Yes. Every completed report includes a "Discuss with AI" tab where you can ask follow-up questions, request deeper exploration of specific patterns, or think through your coaching approach with an AI that has full context of the session.',
  },
  {
    q: 'Who is Session Intelligence™ designed for?',
    a: 'Session Intelligence™ is designed for professional coaches who want to use AI responsibly to enhance their practice — not replace it. It is suitable for life coaches, business coaches, NLP practitioners, and other helping professionals who work with clients in structured conversations.',
  },
];

const REPORT_SECTIONS = [
  'Session Summary', 'Key Themes', 'Limiting Beliefs', 'Meta Model Patterns',
  'Emotional Language Signals', 'Parts in Conflict', 'Contradictions & Tensions',
  'Identity-Level Statements', 'Values & Motivation Clues', 'Avoidance & Resistance Patterns',
  'Breakthrough Zone', 'Suggested Coach Questions (10–15)',
  'Suggested Next Session Focus', 'Important Cautions',
];

const SIGNAL_CARDS = [
  {
    icon: Brain,
    title: 'Limiting Beliefs',
    body: 'Identifies statements where the client may be describing reality through a limiting frame — and surfaces the possible belief behind the language.',
    example: '"I\'m just not confident." → Possible fixed identity frame around confidence as a trait rather than a skill.',
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
  },
  {
    icon: MessageSquare,
    title: 'NLP Meta Model Patterns',
    body: 'Detects generalisations, deletions, distortions, mind reads, modal operators, cause-effect language, and more — with suggested coach questions for each.',
    example: '"People will judge me." → Mind reading / unspecified reference. Coach question: "Which people specifically? How do you know they will judge you?"',
    color: 'bg-violet-50 border-violet-200',
    iconColor: 'text-violet-600',
  },
  {
    icon: AlertTriangle,
    title: 'Emotional Language Signals',
    body: 'Highlights emotionally charged language, repeated words, and themes related to fear, shame, guilt, anxiety, overwhelm, resentment, and more.',
    example: 'Client repeatedly uses language around "pressure" and "not being good enough" — possible emotional theme to explore with care.',
    color: 'bg-amber-50 border-amber-200',
    iconColor: 'text-amber-600',
  },
  {
    icon: GitBranch,
    title: 'Parts in Conflict',
    body: 'Identifies possible internal tensions where one part of the client wants something while another part resists, avoids, or wants the opposite.',
    example: '"I want to grow, but I hate selling." → Possible parts conflict between ambition and avoidance of judgment.',
    color: 'bg-rose-50 border-rose-200',
    iconColor: 'text-rose-600',
  },
  {
    icon: Eye,
    title: 'Contradictions & Tensions',
    body: 'Surfaces moments where the client\'s stated goals and actual language do not fully align — and suggests how the coach might explore the gap.',
    example: 'Client says they want more clients but also says they don\'t want to post online, follow up, sell, or be seen.',
    color: 'bg-orange-50 border-orange-200',
    iconColor: 'text-orange-600',
  },
  {
    icon: Users,
    title: 'Identity-Level Statements',
    body: 'Notices when a client treats a temporary behaviour, skill gap, or state as a fixed identity — and suggests a possible reframe direction.',
    example: '"I\'m just not disciplined." → Treating a habit pattern as a permanent personal trait.',
    color: 'bg-teal-50 border-teal-200',
    iconColor: 'text-teal-600',
  },
  {
    icon: Shield,
    title: 'Values & Motivation Clues',
    body: 'Highlights what appears to matter most to the client — freedom, security, recognition, impact, belonging — and how it may be shaping their choices.',
    example: 'Client consistently returns to language around "safety" and "not losing what I have" → security as a dominant value clue.',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-600',
  },
  {
    icon: Brain,
    title: 'Avoidance & Resistance Patterns',
    body: 'Detects where the client becomes vague, changes subject, laughs things off, intellectualises, or repeatedly says "I don\'t know."',
    example: 'Client gives thorough context for every decision but avoids any question about how they feel about the situation.',
    color: 'bg-slate-50 border-slate-200',
    iconColor: 'text-slate-600',
  },
  {
    icon: Zap,
    title: 'Breakthrough Zone',
    body: 'At the end of every report, Session Intelligence™ identifies the single area most likely to produce deep leverage — and suggests a concrete next step for the coach.',
    example: 'Breakthrough Zone: Self-worth and visibility. Reasoning: Every goal the client has is blocked by the same fear of being seen and judged.',
    color: 'bg-yellow-50 border-yellow-300',
    iconColor: 'text-yellow-600',
  },
];

const FOR_WHO = [
  'You want to understand your clients more deeply',
  'You want to ask better questions',
  'You want to spot limiting beliefs more quickly',
  'You want to notice patterns across sessions',
  'You want to prepare more intelligently before each session',
  'You want to bring AI into your practice without losing the human element',
  'You want to create a more structured, professional, and modern coaching experience',
];

const NOT_FOR_WHO = [
  'You want AI to diagnose your clients',
  'You want AI to replace your coaching skill',
  'You do not want to use client consent and ethical data handling',
  'You are looking for a therapy or mental health assessment tool',
  'You want generic AI summaries instead of structured coaching insight',
];

export function SILandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [wName, setWName] = useState('');
  const [wEmail, setWEmail] = useState('');
  const [wSubmitting, setWSubmitting] = useState(false);
  const [wDone, setWDone] = useState(false);
  const [wError, setWError] = useState('');

  const handleWalkthroughSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWSubmitting(true);
    setWError('');
    const { error } = await supabase.from('si_walkthrough_requests' as never).insert({ name: wName, email: wEmail } as never);
    setWSubmitting(false);
    if (error) { setWError('Something went wrong. Please try again.'); return; }
    setWDone(true);
  };

  const openWalkthrough = () => { setShowModal(true); setWDone(false); setWName(''); setWEmail(''); setWError(''); };

  return (
    <div className="min-h-screen bg-white font-urbanist text-gray-80">

      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-20">
        <div className="section-container flex items-center justify-between h-16">
          <div className="flex items-center">
            <span className="text-sm font-semibold text-gray-80">Session Intelligence<sup className="text-[0.6em]">™</sup></span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/si/login" className="text-sm font-medium text-gray-60 hover:text-gray-80 transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="pt-20 pb-24 bg-gradient-to-b from-brand-5 to-white">
        <div className="section-container text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <div className="inline-flex items-center gap-2 bg-success-10 text-success-70 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
              <Zap className="w-3.5 h-3.5" />
              Session Intelligence<sup className="text-[0.6em]">™</sup> — Session Intelligence<sup className="text-[0.6em]">™</sup>
            </div>
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="text-4xl md:text-6xl font-bold text-gray-80 leading-tight mb-6 max-w-4xl mx-auto"
          >
            See What Your Client's Language<br className="hidden md:block" />
            <span className="text-success-50"> May Be Revealing</span><br className="hidden md:block" />
            Beneath the Surface
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-lg md:text-xl text-gray-50 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            AI-assisted coaching session analysis that helps you identify possible limiting beliefs, emotional patterns, contradictions, parts conflicts, Meta Model patterns, and breakthrough opportunities from real coaching conversations.
          </motion.p>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="text-base text-gray-60 max-w-xl mx-auto mb-10"
          >
            Turn your coaching sessions into clearer insight, sharper next-session preparation, and deeper transformational direction.
          </motion.p>
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={4}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button onClick={openWalkthrough} className="btn-primary text-base px-8 py-3.5">
              Book a Platform Walkthrough
            </button>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-20 bg-white">
        <div className="section-container max-w-3xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-6">
              Most Coaches Rely on Memory After a Session
            </h2>
            <p className="text-gray-50 text-lg leading-relaxed mb-8">
              A client may speak for 45, 60, or 90 minutes — revealing beliefs, fears, values, contradictions, identity statements, emotional charge, and internal conflicts, often in subtle ways.
            </p>
            <p className="text-gray-50 text-lg leading-relaxed mb-10">
              But after the session, most coaches are left with their memory, a few notes, intuition, and a general sense of what happened.
            </p>
            <p className="text-gray-60 font-medium mb-6">A client may say, across one session:</p>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="bg-brand-5 rounded-3xl p-8 space-y-3 mb-10"
          >
            {[
              '"I really want more clients."',
              '"I hate selling."',
              '"I don\'t want people to think I\'m pushy."',
              '"I\'m just not confident online."',
              '"I know what to do, but I don\'t do it."',
            ].map((quote, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-success-40 mt-2.5 flex-shrink-0" />
                <p className="text-gray-80 text-lg italic">{quote}</p>
              </div>
            ))}
          </motion.div>

          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="text-gray-50 text-lg leading-relaxed"
          >
            To the untrained ear, these may sound like separate comments. But together, they may point to a deeper pattern. That is where Session Intelligence™ helps.
          </motion.p>
        </div>
      </section>

      {/* WHAT IS IT */}
      <section className="py-20 bg-brand-5">
        <div className="section-container max-w-4xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-4">What Is Session Intelligence<sup className="text-xl font-normal">™</sup>?</h2>
            <p className="text-gray-50 text-lg leading-relaxed mb-10">
              Session Intelligence™ is an AI-assisted coaching analysis tool inside Session Intelligence™. Upload a session audio file, transcript, or notes and receive a structured coaching insight report.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: '🎙', title: 'Audio Recording', desc: 'MP3, M4A, WAV, OGG · up to 200 MB · auto-deleted after transcription' },
              { icon: '📄', title: 'Document / Transcript', desc: 'PDF, Word (.docx), Plain Text — upload existing transcripts' },
              { icon: '📝', title: 'Session Notes', desc: 'Lower confidence analysis from coach notes when no transcript is available' },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
                className="bg-white rounded-3xl p-6 border border-gray-20 shadow-sm"
              >
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="font-semibold text-gray-80 mb-2">{item.title}</h3>
                <p className="text-gray-50 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
            className="mt-10 bg-white border border-gray-20 rounded-3xl p-8"
          >
            <h3 className="font-bold text-gray-80 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-success-50" />
              This Is Not Therapy. This Is Not Diagnosis.
            </h3>
            <p className="text-gray-50 leading-relaxed">
              Session Intelligence™ gives the coach structured coaching hypotheses based on the client's language — not labels, not clinical assessments. The report uses careful language: "This may suggest…", "A possible pattern is…", "The coach may want to explore…". The coach remains responsible for validating every insight with the client.
            </p>
          </motion.div>
        </div>
      </section>

      {/* HIDDEN GOLD */}
      <section className="py-20 bg-white">
        <div className="section-container max-w-4xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-4">The Hidden Gold Is Often in the Client's Language</h2>
            <p className="text-gray-50 text-lg leading-relaxed mb-10">
              Clients rarely announce their limiting beliefs directly. They reveal them through everyday language.
            </p>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                intro: 'They may not say they have a limiting belief around visibility. They may say:',
                quotes: ['"I don\'t want to put myself out there."', '"People will judge me."', '"I\'m not really the type of person who promotes myself."', '"I know I should post more, but something stops me."'],
              },
              {
                intro: 'They may not say they have a parts conflict. They may say:',
                quotes: ['"Part of me wants to grow, but part of me just wants to stay safe."', '"I want success, but I don\'t want the pressure."', '"I want to be seen, but I also want to hide."'],
              },
              {
                intro: 'They may not say they are operating from a fixed identity frame. They may say:',
                quotes: ['"I\'m just not confident."', '"I\'m a procrastinator."', '"I\'m not a business person."', '"I\'m too emotional."'],
              },
            ].map((block, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.5}
              >
                <p className="text-gray-60 font-medium mb-3">{block.intro}</p>
                <div className="bg-brand-5 rounded-2xl p-6 space-y-2">
                  {block.quotes.map((q, j) => (
                    <p key={j} className="text-gray-80 italic">{q}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
            className="mt-10 text-lg text-gray-50 leading-relaxed"
          >
            Session Intelligence™ helps the coach notice these patterns, organise them, and prepare better questions for the next session.
          </motion.p>
        </div>
      </section>

      {/* 9 SIGNALS */}
      <section className="py-20 bg-brand-5">
        <div className="section-container">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-4">What Session Intelligence<sup className="text-xl font-normal">™</sup> Looks For</h2>
            <p className="text-gray-50 text-lg max-w-2xl mx-auto">Nine layers of coaching signal, each grounded in the client's actual language.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SIGNAL_CARDS.map((card, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.05}
                className={`rounded-3xl p-6 border ${card.color}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-white/70 ${card.iconColor}`}>
                  <card.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-80 mb-2">{card.title}</h3>
                <p className="text-gray-60 text-sm leading-relaxed mb-4">{card.body}</p>
                <div className="bg-white/70 rounded-xl p-3">
                  <p className="text-xs text-gray-50 leading-relaxed italic">{card.example}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REPORT STRUCTURE */}
      <section className="py-20 bg-white">
        <div className="section-container max-w-4xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-4">What You Receive From a Session Intelligence™ Report</h2>
            <p className="text-gray-50 text-lg">Every report is structured, section by section, around the client's actual language.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-3">
            {REPORT_SECTIONS.map((section, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.03}
                className="flex items-center gap-3 bg-brand-5 rounded-2xl px-5 py-3"
              >
                <span className="w-6 h-6 rounded-full bg-success-40 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <span className="text-gray-80 font-medium text-sm">{section}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY COACHES USE IT */}
      <section className="py-20 bg-brand-5">
        <div className="section-container">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-4">Why Coaches Use Session Intelligence™</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Prepare Better for the Next Session', body: 'Instead of relying only on memory, review a structured analysis before seeing the client again. Know exactly where to focus.' },
              { title: 'Spot Patterns You May Have Missed', body: "The client's language may reveal repeated themes that were not obvious in the moment — visible only when the full session is reviewed together." },
              { title: 'Ask Better Questions', body: 'Receive 10–15 suggested questions based on what the client actually said — grounded in real language, not generic coaching scripts.' },
              { title: 'Deepen Your Coaching Precision', body: 'Move from general coaching direction to specific language-based exploration. Know which thread to pull, and why.' },
              { title: 'Support Your Human Intuition', body: "Session Intelligence™ does not replace intuition. It helps organise and strengthen it — giving structure to what you sensed in the room." },
              { title: 'Create a More Professional Coaching Process', body: 'Clients expect personalisation, structure, and continuity. Session Intelligence™ helps you bring more depth, consistency, and rigour to your work.' },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.05}
                className="bg-white rounded-3xl p-6 border border-gray-20 shadow-sm"
              >
                <h3 className="font-semibold text-gray-80 mb-3">{item.title}</h3>
                <p className="text-gray-50 text-sm leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HUMAN-LED */}
      <section className="py-20 bg-gray-80 text-white">
        <div className="section-container max-w-4xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built for Human-Led, AI-Enhanced Coaching</h2>
            <p className="text-gray-30 text-lg max-w-2xl mx-auto">Session Intelligence™ is built on a simple principle: technology should not replace the human coach. It should enhance the human coach.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="bg-white/10 rounded-3xl p-8"
            >
              <h3 className="font-semibold text-success-40 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5" /> AI can
              </h3>
              <ul className="space-y-3">
                {['Analyse language patterns', 'Identify possible emotional themes', 'Spot repeated contradictions', 'Suggest relevant questions', 'Organise a structured report'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-20 text-sm">
                    <Check className="w-4 h-4 text-success-40 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
              className="bg-white/10 rounded-3xl p-8"
            >
              <h3 className="font-semibold text-brand-40 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> You provide
              </h3>
              <ul className="space-y-3">
                {['Human presence and empathy', 'Relationship and trust', 'Professional judgement', 'Validation with the client', 'Transformational skill'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-20 text-sm">
                    <Check className="w-4 h-4 text-brand-40 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
            className="text-center text-gray-30 text-lg mt-10 italic"
          >
            AI can analyse language. But you hold the human relationship. That is the difference.
          </motion.p>
        </div>
      </section>

      {/* FOR / NOT FOR */}
      <section className="py-20 bg-white">
        <div className="section-container max-w-4xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-4">Who Is This For?</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="bg-success-5 border border-success-10 rounded-3xl p-8"
            >
              <h3 className="font-bold text-success-70 mb-5">This is for you if...</h3>
              <ul className="space-y-3">
                {FOR_WHO.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-60 text-sm">
                    <Check className="w-4 h-4 text-success-50 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
              className="bg-red-50 border border-red-100 rounded-3xl p-8"
            >
              <h3 className="font-bold text-red-700 mb-5">This is not for you if...</h3>
              <ul className="space-y-3">
                {NOT_FOR_WHO.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-60 text-sm">
                    <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRIVACY NOTE */}
      <section className="py-12 bg-brand-5">
        <div className="section-container max-w-3xl">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="bg-white border border-gray-20 rounded-3xl p-8 flex gap-5"
          >
            <Shield className="w-8 h-8 text-success-50 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-80 mb-2">Important Privacy and Consent Note</h3>
              <p className="text-gray-50 text-sm leading-relaxed">
                Before uploading any audio, transcript, or session notes, coaches should ensure they have permission from their client to record, transcribe, and use the material for AI-assisted coaching support. Session Intelligence™ should be used with transparency, care, and professional judgement, following the privacy and data protection requirements that apply in your location.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* NEW STANDARD */}
      <section className="py-20 bg-white">
        <div className="section-container max-w-3xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-6">A New Standard for Coaching Reflection</h2>
            <p className="text-gray-50 text-lg mb-8">Instead of asking "What do I remember from that session?" — you can ask better questions.</p>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="bg-brand-5 rounded-3xl p-8 text-left space-y-3 mb-10"
          >
            {[
              'What did the client\'s language reveal?',
              'What patterns repeated?',
              'What emotional themes showed up?',
              'What beliefs may be shaping behaviour?',
              'What contradictions are worth exploring?',
              'What is the next best coaching question?',
            ].map((q, i) => (
              <div key={i} className="flex items-center gap-3">
                <ArrowRight className="w-4 h-4 text-success-50 flex-shrink-0" />
                <p className="text-gray-60">{q}</p>
              </div>
            ))}
          </motion.div>

          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="text-gray-50 leading-relaxed"
          >
            When preparation improves, the next session can become more focused, more precise, and more transformational.
          </motion.p>
        </div>
      </section>

      {/* HPOS CONTEXT */}
      <section className="py-16 bg-brand-5 border-t border-gray-20">
        <div className="section-container max-w-3xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h3 className="font-semibold text-gray-80 mb-3">Session Intelligence™</h3>
            <p className="text-gray-50 text-sm leading-relaxed max-w-xl mx-auto">
              Session Intelligence™ is one part of Session Intelligence™ — a wider coaching operating system designed to help coaches attract clients, understand the person, identify patterns, support progress, assign pathways, provide human presence, demonstrate proof, and build a more sustainable coaching business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="section-container max-w-3xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-80 mb-4">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.05}
                className="bg-brand-5 rounded-2xl overflow-hidden border border-gray-20"
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium text-gray-80">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-gray-40 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-40 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-50 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-80 text-white">
        <div className="section-container max-w-3xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Turn Client Conversations Into Coaching Intelligence</h2>
            <p className="text-gray-30 text-lg mb-10 max-w-xl mx-auto">
              Your client's language may already contain the clues. Session Intelligence™ helps you organise those clues into clearer insight, better questions, and deeper coaching direction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={openWalkthrough} className="btn-primary text-base px-8 py-3.5">
                Book a Platform Walkthrough
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 bg-gray-80 border-t border-white/10">
        <div className="section-container flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <span className="text-gray-40 text-sm">Session Intelligence™</span>
          </div>
          <div className="flex items-center gap-6 text-gray-40 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
          </div>
          <p className="text-gray-50 text-xs">Copyright 2025 Session Intelligence™, All Rights Reserved.</p>
        </div>
      </footer>

      {/* Walkthrough Request Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative"
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full text-gray-40 hover:text-gray-80 hover:bg-brand-5 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {wDone ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 bg-success-10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-7 h-7 text-success-50" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-80 mb-3">Thank You!</h3>
                  <p className="text-gray-50 leading-relaxed">
                    We'll be in touch shortly to organise a time for your platform walkthrough.
                  </p>
                  <button
                    onClick={() => setShowModal(false)}
                    className="mt-6 btn-primary px-8 py-2.5 text-sm"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-gray-80 mb-1">Book a Platform Walkthrough</h3>
                  <p className="text-sm text-gray-50 mb-6">Leave your details and we'll be in touch to organise a time.</p>
                  <form onSubmit={handleWalkthroughSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-70 mb-1.5">Your Name</label>
                      <input
                        type="text"
                        required
                        value={wName}
                        onChange={e => setWName(e.target.value)}
                        placeholder="Jane Smith"
                        className="w-full px-4 py-2.5 border border-gray-20 rounded-xl text-sm text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors bg-brand-5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-70 mb-1.5">Email Address</label>
                      <input
                        type="email"
                        required
                        value={wEmail}
                        onChange={e => setWEmail(e.target.value)}
                        placeholder="jane@example.com"
                        className="w-full px-4 py-2.5 border border-gray-20 rounded-xl text-sm text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors bg-brand-5"
                      />
                    </div>
                    {wError && <p className="text-sm text-red-600">{wError}</p>}
                    <button
                      type="submit"
                      disabled={wSubmitting}
                      className="w-full btn-primary py-3 text-sm disabled:opacity-60"
                    >
                      {wSubmitting ? 'Submitting…' : 'Request Walkthrough'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
